/**
 * LeadStatusControl — Operational controls for thread lead lifecycle
 *
 * Displays inside ThreadPanel header area:
 * - Human sales stage badge + explicit business actions (SALES-CRM-SIMPLIFICATION)
 * - Lead source selector
 * - Coordinator assignment
 * - Confirmation modal for WON/LOST
 *
 * Calls:
 * - PUT /api/clinic-leads/{lead_id}/status (canonical lead writer; thread mirror projected server-side)
 * - PUT /api/v2/analytics/threads/{id}/lead-source
 * - PUT /api/v2/threads/{id}/assign
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from './ui/badge';
import {
  ChevronDown, Check, Tag, Loader2
} from 'lucide-react';
import api, { clinicLeadAPI } from '../utils/api';
import { SalesStageBadge, SalesActions } from './leads/SalesStage'; // SALES-CRM-SIMPLIFICATION
import { MarkAsWonDialog } from './MarkAsWonDialog';
import i18n from '../i18n/i18n';


const STATUSES = [
  { key: 'NEW', label: 'New', color: 'bg-slate-500 text-white' },
  { key: 'CONTACTED', label: 'Contacted', color: 'bg-sky-500 text-white' },
  { key: 'QUALIFIED', label: 'Qualified', color: 'bg-indigo-500 text-white' },
  { key: 'OFFER_SENT', label: 'Offer Sent', color: 'bg-violet-500 text-white' },
  { key: 'NEGOTIATION', label: 'Negotiation', color: 'bg-amber-500 text-white' },
  { key: 'WON', label: 'Won', color: 'bg-emerald-600 text-white' },
  { key: 'LOST', label: 'Lost', color: 'bg-red-500 text-white' },
];


const SOURCES = [
  { key: 'META_ADS', label: 'Meta Ads' },
  { key: 'GOOGLE_ADS', label: 'Google Ads' },
  { key: 'ORGANIC', label: 'Organic' },
  { key: 'WHATSAPP', label: 'WhatsApp' },
  { key: 'INSTAGRAM', label: 'Instagram' },
  { key: 'COORDINATOR_REFERRAL', label: 'Coordinator Referral' },
  { key: 'OTHER', label: 'Other' },
];

// LIFECYCLE-V3.2 — labels via i18n; unknown status renders a neutral, observable fallback (never "New")
const titleCase = (v) => String(v || '').toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const getStatusConfig = (status) => {
  const found = STATUSES.find(s => s.key === status);
  const label = i18n.exists(`provider.leads.statuses.${status}`) ? i18n.t(`provider.leads.statuses.${status}`) : (found?.label || titleCase(status));
  return found ? { ...found, label } : { key: status, label, color: 'bg-slate-200 text-slate-700', unknown: true };
};

export const LeadStatusBadge = ({ status }) => {
  const cfg = getStatusConfig(status);
  return (
    <Badge className={`${cfg.color} text-[10px] px-2`} data-testid={cfg.unknown ? 'status-unknown' : 'lead-status-badge'}>
      {cfg.label}
    </Badge>
  );
};

export const LeadSourceBadge = ({ source }) => {
  if (!source) return null;
  const src = SOURCES.find(s => s.key === source);
  return (
    <Badge variant="outline" className="text-[10px] px-1.5 border-slate-300 text-slate-600" data-testid="lead-source-badge">
      <Tag className="h-2.5 w-2.5 mr-0.5" />
      {src?.label || source}
    </Badge>
  );
};

export const LeadStatusControl = ({ thread, onUpdate, hideSource = false }) => {
  const [sourceOpen, setSourceOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transitions, setTransitions] = useState(null);
  const [markWonOpen, setMarkWonOpen] = useState(false);

  const currentStatus = thread?.lead_status || 'NEW';
  const currentSource = thread?.lead_source;
  // Lead binding: threads carry context_type='lead' + context_id (SSOT); lead_id is a legacy alias
  const leadId = thread?.lead_id || (thread?.context_type === 'lead' ? thread?.context_id : null);

  // SALES-CRM-SIMPLIFICATION — human stage / context / allowed actions come from the backend projection
  const loadTransitions = useCallback(() => {
    if (!leadId) return;
    api.get(`/clinic-leads/${leadId}/status-transitions`)
      .then(r => setTransitions(r.data))
      .catch(() => setTransitions(null));
  }, [leadId]);
  useEffect(() => { loadTransitions(); }, [loadTransitions, currentStatus]);

  const afterChange = (newStatus) => {
    onUpdate?.({ ...thread, lead_status: newStatus || thread.lead_status });
    loadTransitions();
  };

  const handleStatus = async (newStatus, reason) => {
    setLoading(true);
    try {
      const res = await clinicLeadAPI.updateStatus(leadId, newStatus, reason || undefined);
      afterChange(res.data?.new_status || newStatus);
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = async (newSource) => {
    setSourceOpen(false);
    setLoading(true);
    try {
      const res = await api.put(`/v2/analytics/threads/${thread.id}/lead-source`, { lead_source: newSource });
      onUpdate?.(res.data.thread);
    } catch (err) {
      console.error('Source update error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-nowrap shrink-0 whitespace-nowrap sm:flex-wrap" data-testid="lead-status-control">
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-500" />}

      <SalesStageBadge transitions={transitions} fallbackLabel={getStatusConfig(currentStatus).label} />
      {leadId && (
        <SalesActions
          lead={{ id: leadId, status: currentStatus }}
          transitions={transitions}
          compact
          idSuffix="thread"
          onStatus={handleStatus}
          onMarkWon={() => setMarkWonOpen(true)}
          onDone={(res) => afterChange(res?.new_status)}
        />
      )}
      {markWonOpen && (
        <MarkAsWonDialog lead={{ id: leadId, patient_name: thread.patient_name }} open={markWonOpen} onClose={() => setMarkWonOpen(false)} onDone={() => afterChange('WON')} />
      )}

      {/* Source dropdown — hidden when caller marks `hideSource` (source is set automatically on entry). */}
      {!hideSource && (
      <div className="relative">
        <button
          onClick={() => setSourceOpen(!sourceOpen)}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors"
          data-testid="lead-source-trigger"
        >
          <Tag className="h-3 w-3 text-slate-400" />
          {currentSource ? SOURCES.find(s => s.key === currentSource)?.label || currentSource : 'Set Source'}
          <ChevronDown className="h-3 w-3" />
        </button>
        {sourceOpen && (
          <div className="absolute left-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-[170px] z-30" data-testid="lead-source-dropdown">
            {SOURCES.map(s => (
              <button
                key={s.key}
                onClick={() => handleSourceChange(s.key)}
                className={`w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 flex items-center gap-2 ${
                  s.key === currentSource ? 'bg-slate-100 font-semibold' : 'text-slate-700'
                }`}
                data-testid={`source-option-${s.key}`}
              >
                {s.label}
                {s.key === currentSource && <Check className="h-3 w-3 ml-auto text-slate-500" />}
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {/* The assignee is already stated once by the thread header badge or the
          assign control; a third copy only cost a row on mobile. */}
    </div>
  );
};

export default LeadStatusControl;
